import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import { useState } from "react";
import {
  IApplyContractTermChange,
  useApplyContractModelDraftContractTermMutation,
} from "store/queries/contractModelDrafts";

interface IApplyContractTermPanelProps {
  runId: string;
  /** The accepted `contract_term` recommendation. Null when there isn't one yet. */
  recommendationId: string | null;
  /** The contract this run targets. Null means a new Draft contract would be created. */
  contractFeeScheduleId: string | null;
  /**
   * Optional. The mutation already invalidates the run and the contract caches, so the page
   * refreshes on its own — this is only for a caller that wants to navigate afterwards.
   */
  onApplied?: (contractFeeScheduleId: string | null) => void;
}

const ACTION_LABELS: Record<
  IApplyContractTermChange["action"],
  { label: string; severity: "success" | "info" | "warning" }
> = {
  filled: { label: "Will fill", severity: "success" },
  appended: { label: "Will append", severity: "info" },
  skipped: { label: "Left as is", severity: "warning" },
};

const FIELD_LABELS: Record<string, string> = {
  contractType: "Contract type",
  signedDate: "Signed date",
  timelyFilingDays: "Timely filing (days)",
  implantTerms: "Implant terms",
  inpatientReimbursement: "Inpatient reimbursement",
  outpatientReimbursement: "Outpatient reimbursement",
  facilityReimbursement: "Facility reimbursement",
  nonFacilityReimbursement: "Non-facility reimbursement",
  nextUpdateDate: "Next update date",
  updateTerm: "Update term",
  updatePercent: "Percent increase",
  notes: "Notes",
};

/**
 * CLE-2811 — write the agent's contract facts onto a real contract.
 *
 * Preview is a separate, mandatory step rather than a confirm dialog. This is the only control in
 * the tool that writes to a production pricing table, and the reviewer needs to see WHICH fields it
 * would touch before it touches them — a generic "are you sure?" tells her nothing about whether the
 * agent is about to fill a field she already answered.
 *
 * The one rule worth repeating in the UI: a field the contract already has is never overwritten. It
 * shows as "Left as is" so she can see the agent had a value and her own survived.
 */
export function ApplyContractTermPanel({
  runId,
  recommendationId,
  contractFeeScheduleId,
  onApplied,
}: IApplyContractTermPanelProps) {
  const [applyContractTerm, { isLoading }] =
    useApplyContractModelDraftContractTermMutation();
  const [preview, setPreview] = useState<IApplyContractTermChange[] | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const [savedTo, setSavedTo] = useState<string | null>(null);

  if (!recommendationId) {
    return (
      <div className="mt-4 pt-3 border-top-1 surface-border">
        <Message
          severity="info"
          text="Accept the contract term above to save its details onto the contract."
        />
      </div>
    );
  }

  const isUpdate = !!contractFeeScheduleId;

  const run = async (dryRun: boolean) => {
    setError(null);
    try {
      const result = await applyContractTerm({
        runId,
        recommendationId,
        dryRun,
      }).unwrap();
      if (dryRun) {
        setPreview(result.changes);
      } else {
        setSavedTo(result.contractFeeScheduleId);
        setPreview(null);
        onApplied?.(result.contractFeeScheduleId);
      }
    } catch (err: any) {
      setError(
        err?.data?.message ?? "Could not write the contract term to a contract."
      );
    }
  };

  if (savedTo) {
    return (
      <div className="mt-4 pt-3 border-top-1 surface-border">
        <Message
          severity="success"
          text={
            isUpdate
              ? "Saved to the contract. Fields that already had a value were left alone."
              : "A draft contract was created. Review and activate it in Payer Rules."
          }
        />
      </div>
    );
  }

  const willChange = (preview ?? []).filter((c) => c.action !== "skipped");

  return (
    <div className="mt-4 pt-3 border-top-1 surface-border">
      <div className="flex align-items-center justify-content-between gap-3 flex-wrap">
        <div>
          <div className="font-medium text-900">
            {isUpdate
              ? "Save these details to the contract"
              : "Create a draft contract from these details"}
          </div>
          <small className="text-color-secondary">
            {isUpdate
              ? "Fills only the fields the contract has left empty, and appends to its notes. Nothing already recorded is overwritten, and no rates change."
              : "Creates a Draft contract with these terms and no rate calculations. A draft prices nothing until someone activates it."}
          </small>
        </div>
        <div className="flex gap-2">
          <Button
            label="Preview changes"
            icon="pi pi-eye"
            className="p-button-outlined"
            loading={isLoading && preview === null}
            onClick={() => run(true)}
          />
          {preview !== null && (
            <Button
              label={isUpdate ? "Save to contract" : "Create draft contract"}
              icon="pi pi-check"
              disabled={willChange.length === 0}
              tooltip={
                willChange.length === 0
                  ? "The contract already has every field the agent found"
                  : undefined
              }
              loading={isLoading}
              onClick={() => run(false)}
            />
          )}
        </div>
      </div>

      {error && <Message severity="error" text={error} className="mt-3" />}

      {preview !== null && preview.length === 0 && (
        <Message
          severity="info"
          text="The agent recorded nothing that belongs on the contract."
          className="mt-3"
        />
      )}

      {preview !== null && preview.length > 0 && (
        <div className="mt-3 flex flex-column gap-2">
          {preview.map((change) => (
            <div
              key={change.field}
              className="flex align-items-start gap-3 p-2 surface-50 border-round"
            >
              <Tag
                value={ACTION_LABELS[change.action].label}
                severity={ACTION_LABELS[change.action].severity}
              />
              <div className="flex-1">
                <div className="font-medium text-900">
                  {FIELD_LABELS[change.field] ?? change.field}
                </div>
                <div className="text-sm text-color-secondary">
                  {change.action === "skipped" ? (
                    <>Keeping: {change.from ?? "not recorded"}</>
                  ) : (
                    <>
                      {change.from ? `${change.from} → ` : ""}
                      {change.to ?? "not recorded"}
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ApplyContractTermPanel;
